import React, { useEffect, useState, createContext, useContext } from 'react';
import { Order, OrderStatus, Tour, TimelineEntry, SuggestedTour } from '../types';
import { storage } from '../utils/storage';
import { useAuth } from './AuthContext';
import { generateSuggestedTours, calculateOrderPriority, getNextDeliveryDate } from '../utils/tourGenerator';
interface OrderContextType {
  orders: Order[];
  tours: Tour[];
  refreshData: () => void;
  createOrder: (orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt' | 'timeline' | 'status' | 'priority' | 'deliveryAttempts'>) => Promise<Order>;
  updateOrderStatus: (orderId: string, newStatus: OrderStatus, action: string, notes?: string) => Promise<void>;
  assignStorage: (orderId: string, zone: string, shelf: string) => Promise<void>;
  createTour: (tourData: Omit<Tour, 'id' | 'createdAt' | 'status'>) => Promise<Tour>;
  getOrdersByStatus: (status: OrderStatus) => Order[];
  getOrdersByZone: (zone: string) => Order[];
  markOrderAsReturn: (orderId: string, reason: string) => Promise<void>;
  getSuggestedTours: () => SuggestedTour[];
  approveSuggestedTour: (suggestion: SuggestedTour, driverId: string, vehicle: string) => Promise<Tour>;
}
const OrderContext = createContext<OrderContextType | undefined>(undefined);
export function OrderProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const refreshData = () => {
    setOrders(storage.getOrders());
    setTours(storage.getTours());
  };
  useEffect(() => {
    refreshData();
  }, []);
  const createOrder = async (orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt' | 'timeline' | 'status' | 'priority' | 'deliveryAttempts'>): Promise<Order> => {
    if (!user) throw new Error('User must be logged in');
    const newOrder: Order = {
      ...orderData,
      id: `CMD-${Date.now().toString().slice(-6)}`,
      status: 'CREEE',
      priority: 0,
      deliveryAttempts: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      timeline: [{
        date: new Date().toISOString(),
        userId: user.id,
        userName: user.name,
        action: 'Création de la commande',
        status: 'CREEE'
      }]
    };
    storage.saveOrder(newOrder);
    refreshData();
    return newOrder;
  };
  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus, action: string, notes?: string) => {
    if (!user) throw new Error('User must be logged in');
    const currentOrders = storage.getOrders();
    const orderIndex = currentOrders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) throw new Error('Order not found');
    const order = currentOrders[orderIndex];
    // Add timeline entry
    const newTimelineEntry: TimelineEntry = {
      date: new Date().toISOString(),
      userId: user.id,
      userName: user.name,
      action: action,
      status: newStatus
    };
    // Increment delivery attempts if going back to EN_TOURNEE from RETOUR_DEPOT
    let deliveryAttempts = order.deliveryAttempts;
    if (order.status === 'RETOUR_DEPOT' && newStatus === 'EN_TOURNEE') {
      deliveryAttempts += 1;
    }
    const updatedOrder: Order = {
      ...order,
      status: newStatus,
      deliveryAttempts,
      timeline: [...order.timeline, newTimelineEntry],
      updatedAt: new Date().toISOString(),
      notes: notes ? `${order.notes ? order.notes + '\n' : ''}${notes}` : order.notes,
      priority: calculateOrderPriority({
        ...order,
        status: newStatus,
        deliveryAttempts
      })
    };
    storage.saveOrder(updatedOrder);
    refreshData();
  };
  const markOrderAsReturn = async (orderId: string, reason: string) => {
    if (!user) throw new Error('User must be logged in');
    const currentOrders = storage.getOrders();
    const orderIndex = currentOrders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) throw new Error('Order not found');
    const order = currentOrders[orderIndex];
    const newTimelineEntry: TimelineEntry = {
      date: new Date().toISOString(),
      userId: user.id,
      userName: user.name,
      action: `Retour dépôt - Raison: ${reason}`,
      status: 'RETOUR_DEPOT'
    };
    const updatedOrder: Order = {
      ...order,
      status: 'RETOUR_DEPOT',
      timeline: [...order.timeline, newTimelineEntry],
      updatedAt: new Date().toISOString(),
      notes: `${order.notes ? order.notes + '\n' : ''}RETOUR: ${reason}`,
      priority: calculateOrderPriority({
        ...order,
        status: 'RETOUR_DEPOT'
      })
    };
    storage.saveOrder(updatedOrder);
    refreshData();
  };
  const assignStorage = async (orderId: string, zone: string, shelf: string) => {
    if (!user) throw new Error('User must be logged in');
    const currentOrders = storage.getOrders();
    const orderIndex = currentOrders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) throw new Error('Order not found');
    const order = currentOrders[orderIndex];
    const updatedOrder: Order = {
      ...order,
      storageLocation: {
        zone,
        shelf
      },
      updatedAt: new Date().toISOString()
    };
    storage.saveOrder(updatedOrder);
    refreshData();
  };
  const createTour = async (tourData: Omit<Tour, 'id' | 'createdAt' | 'status'>): Promise<Tour> => {
    if (!user) throw new Error('User must be logged in');
    const newTour: Tour = {
      ...tourData,
      id: `TR-${Date.now().toString().slice(-6)}`,
      status: 'EN_COURS',
      createdAt: new Date().toISOString()
    };
    storage.saveTour(newTour);
    // Update all orders in the tour to EN_TOURNEE if it's a delivery tour
    if (tourData.type === 'LIVRAISON') {
      for (const orderId of tourData.orderIds) {
        await updateOrderStatus(orderId, 'EN_TOURNEE', `Assigné à la tournée ${newTour.id}`);
      }
    }
    refreshData();
    return newTour;
  };
  const getSuggestedTours = (): SuggestedTour[] => {
    const drivers = storage.getUsers().filter(u => u.role === 'CHAUFFEUR' && u.active);
    const nextDate = getNextDeliveryDate();
    return generateSuggestedTours(orders, drivers, nextDate);
  };
  const approveSuggestedTour = async (suggestion: SuggestedTour, driverId: string, vehicle: string): Promise<Tour> => {
    if (!user) throw new Error('User must be logged in');
    const newTour: Tour = {
      id: `TR-${Date.now().toString().slice(-6)}`,
      type: 'LIVRAISON',
      zone: suggestion.zone,
      driverId,
      vehicle,
      orderIds: suggestion.orderIds,
      status: 'EN_COURS',
      date: suggestion.date,
      createdAt: new Date().toISOString(),
      isAutoGenerated: true
    };
    storage.saveTour(newTour);
    // Update all orders to EN_TOURNEE
    for (const orderId of suggestion.orderIds) {
      await updateOrderStatus(orderId, 'EN_TOURNEE', `Assigné à la tournée auto-générée ${newTour.id}`);
    }
    refreshData();
    return newTour;
  };
  const getOrdersByStatus = (status: OrderStatus) => orders.filter(o => o.status === status);
  const getOrdersByZone = (zone: string) => orders.filter(o => o.zone === zone);
  return <OrderContext.Provider value={{
    orders,
    tours,
    refreshData,
    createOrder,
    updateOrderStatus,
    assignStorage,
    createTour,
    getOrdersByStatus,
    getOrdersByZone,
    markOrderAsReturn,
    getSuggestedTours,
    approveSuggestedTour
  }} data-id="element-226">
      {children}
    </OrderContext.Provider>;
}
export function useOrders() {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
}